import { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { 
    Box,
    Button,
    IconButton,
    Input,
    InputAdornment,
    Stack,
    Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import CloseIcon from '@mui/icons-material/Close';

import RetailerContext from '../../context';

import './index.scss';
import Filters from '../Filters';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useQuery } from '../../hooks/useQuery';
import { hexToRgb, isLight } from '../../utils/colour';

const SearchNav = (props) => {
    const { handleClearSearch, loading, compact=false } = props;
    const { t } = useTranslation();
    const history = useHistory();

    const retailerContext = useContext(RetailerContext);

    const [filters, setFilters] = useState({});
    const [tabIndex, setTabIndex] = useState(null);

    const query = useQuery();
    const filterParams = JSON.parse(query.get('filter') || JSON.stringify({}));
    const searchParam = query.get('q') || '';

    const [searchString, setSearchString] = useState(searchParam);

    useEffect(() => {
        setSearchString(searchParam);
    }, [searchParam]);

    useEffect(() => {
        setFilters(filterParams);
    }, [query.get('filter')]);

    const clearSearch = () => {
        setSearchString('');
        onUpdateFilters({});
        onUpdateTabIndex(0);

        handleClearSearch();
        
        history.push({
            pathname: '',
            search: '?' + new URLSearchParams({}).toString()
        });
    }

    const onUpdateFilters = (filters) => {
        console.log('filters', filters);

        setFilters(filters);
    }

    const onUpdateTabIndex = (tabIndex) => {
        setTabIndex(tabIndex);
    }

    const handleClick = () => {
        const mergedFilters = {
            ...filterParams,
            ...filters,
        };

        history.push({
            pathname: '',
            search: '?' + new URLSearchParams({
                q: searchString,
                tabIndex,
                filter: JSON.stringify(mergedFilters),
            }).toString()
        });
    }

    const compactSearchInput = (
        <Box className='search-nav'>
            <Stack
                direction={'row'}
                sx={{
                    my: compact ? 0 : 3
                }}
            >
                <Input
                    className="font-primary search_input"
                    sx={{
                        color: '#262626 !important',
                        height: '3rem',
                        p: 2,
                        width: '100%'
                    }}
                    value={searchString}
                    onChange={(event) => { setSearchString(event.target.value) }}
                    disabled={loading}
                    placeholder={t('search').toLocaleUpperCase()}
                    variant="filled"
                    endAdornment={
                        searchString &&
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="clear search field"
                                onClick={clearSearch}
                                onMouseDown={clearSearch}
                                disabled={loading}
                            >
                                {<CloseIcon fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    }
                />

                <Button
                    sx={{
                        borderRadius: 0,
                        backgroundColor: retailerContext.retailer.button_background_color,
                        fontFamily: retailerContext.retailer.button_font,
                        height: '3rem',
                        textTransform: 'uppercase',
                        ':hover': {
                            bgcolor: retailerContext.retailer.button_background_color,
                        },
                    }}
                    disabled={loading}
                    variant="contained"
                    onClick={handleClick}
                >
                    <Search />
                </Button>
            </Stack>
        </Box>
    )

    if (compact) {
        return compactSearchInput
    }

    return (
        <>
            <Box className='search-nav'>
                <Stack
                    spacing={1}
                    sx={{
                        my: compact ? 0 : 3
                    }}
                >
                    {
                        !!!compact &&
                        <Typography
                            variant='h3'
                            className='search-header'
                            sx={{
                                textAlign: 'center'
                            }}
                        >
                            {t('findARecipe')}
                        </Typography>
                    }

                    <Input
                        className="font-primary search_input"
                        sx={{
                            color: '#262626 !important',
                            height: '42px',
                            p: 2,
                            width: '100%'
                        }}
                        value={searchString}
                        onChange={(event) => { setSearchString(event.target.value) }}
                        disabled={loading}
                        placeholder={t('search').toLocaleUpperCase()}
                        variant="filled"
                        endAdornment={
                            searchString &&
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="clear search field"
                                    onClick={clearSearch}
                                    onMouseDown={clearSearch}
                                    disabled={loading}
                                >
                                    {<CloseIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />

                    <Filters
                        key={JSON.stringify(filterParams)}
                        onUpdateFilters={onUpdateFilters}
                        onUpdateTabIndex={onUpdateTabIndex}
                        onClearFilters={clearSearch}
                    />

                    {
                        !!!compact &&
                        <Button
                            sx={{
                                borderRadius: 0,
                                backgroundColor: retailerContext.retailer.button_background_color,
                                color: isLight(hexToRgb(retailerContext.retailer.button_background_color)) ? 'black' : 'white',
                                fontFamily: retailerContext.retailer.button_font,
                                height: '48px',
                                textTransform: 'uppercase',
                                ':hover': {
                                    bgcolor: retailerContext.retailer.button_background_color,
                                },
                            }}
                            disabled={loading || !(searchString || Object.keys(filters)?.length > 1)}
                            variant="contained"
                            onClick={handleClick}
                        >
                            {t('search_filterYourResults')}
                        </Button>
                    }
                </Stack>
            </Box>
        </>
    )
};

SearchNav.defaultProps = {
    cooking_methods: [] ,
    nutrition: null,
    searchString: '',
    searchText: '',
    handleClearSearch: () => {},
    triggerSearch: () => {},
}

export default SearchNav;